import classcat from "classcat";
import React from "react";

import {
  IconCog,
  IconLink,
  IconLinkRemove,
  IconStar,
} from "@touchtech/web-baselayer-ui";

import DrawerActionButton from "@is/components/DrawerActionButton/DrawerActionButton";
import { Logo } from "@is/components/Logo";
import ResumeBasketModal from "@is/components/ResumeBasketModal";
import { StoreDevice } from "@is/types";
import { convertToRelativeTime } from "@is/utils";

import InformationItem from "../InformationItem";
import ParkedBasketItem from "../ParkedBasketItem";
import ToolItem from "../ToolItem";
import css from "./SettingsPage.module.css";
import useManageSettingsPage from "./hooks/useManageSettingsPage";

type Props = {
  storeInfo: StoreDevice;
};

const SettingsPage = ({ storeInfo }: Props) => {
  const {
    isScrolled,
    handleClose,
    handleScroll,
    t,
    handleIFramePageOpen,
    handleStoreFavoritesOpen,
    handleScanner,
    scannerDescription,
    isScannerConnected,
    fullDeviceName,
    externalStoreId,
    deviceId,
    storeName,
    locale,
    handleShowResumeParkedBasketModal,
    showResumeParkedBasketModal,
    handleCloseResumeParkedBasketModal,
    handleResumeParkedBasket,
    parkedBaskets,
  } = useManageSettingsPage({ storeInfo });

  return (
    <div>
      <DrawerActionButton onClick={handleClose} isScrolled={isScrolled} />
      <div className={css.container} onScroll={handleScroll}>
        <div className={css.header}>
          <h1 className="text-headline-5xl">{t("Staff dashboard")}</h1>
        </div>
        <div className={css.content}>
          <div>
            <div className={classcat(["text-label-lg", css.subtitle])}>
              {t("Staff tools")}
            </div>
            <div className={css.toolsContainer}>
              <ToolItem
                title={t("Staff portal")}
                description={t("Go to staff portal")}
                onClick={handleIFramePageOpen}
                icon={<IconCog />}
              />
              <ToolItem
                title={t("Connect scanner")}
                description={scannerDescription}
                onClick={handleScanner}
                icon={isScannerConnected ? <IconLinkRemove /> : <IconLink />}
              />
              <ToolItem
                title={t("Store favorites")}
                description={t("Set your store's favorite items")}
                onClick={handleStoreFavoritesOpen}
                icon={<IconStar />}
              />
            </div>
          </div>
          {!!parkedBaskets?.length && (
            <div>
              <div className={classcat(["text-label-lg", css.subtitle])}>
                {t("Parked baskets")}
              </div>
              <div className={css.informationContainer}>
                {parkedBaskets?.map((parkedBasket) => (
                  <ParkedBasketItem
                    key={parkedBasket.id}
                    basketId={parkedBasket.id}
                    basketName={parkedBasket.sessionName}
                    deviceName={`${parkedBasket.deviceName} / ${parkedBasket.deviceType}`}
                    quantity={`${parkedBasket.basketItemsCount} ${
                      parkedBasket.basketItemsCount > 1 ? t("items") : t("item")
                    }`}
                    total={`${parkedBasket.basketTotalPrice}${parkedBasket.currencyCode}`}
                    thumbnails={parkedBasket.thumbnails}
                    expiration={convertToRelativeTime(
                      parkedBasket.createdAt,
                      locale,
                      t
                    )}
                    onResumeClick={handleShowResumeParkedBasketModal}
                  />
                ))}
              </div>
            </div>
          )}
          <div>
            <div className={classcat(["text-label-lg", css.subtitle])}>
              {t("Information")}
            </div>
            <div className={css.informationContainer}>
              <InformationItem
                title={t("Store")}
                name={storeName ?? ""}
                id={externalStoreId ?? ""}
              />
              <InformationItem
                title={t("Device")}
                name={fullDeviceName}
                id={deviceId ?? ""}
              />
            </div>
          </div>
        </div>
        <Logo className={css.footer} />
      </div>
      <ResumeBasketModal
        isOpen={showResumeParkedBasketModal}
        onClose={handleCloseResumeParkedBasketModal}
        onResume={handleResumeParkedBasket}
      />
    </div>
  );
};

export default SettingsPage;
